import { html, css, LitElement, nothing } from 'lit';
import '@lit-labs/virtualizer';
import { createRef, ref } from 'lit/directives/ref.js';

import '../paper-table/paper-table.js';
import '../user-chip/user-chip.js';
import '../highlight-text/highlight-text.js';
import '@polymer/paper-checkbox/paper-checkbox.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/resize-observer/resize-observer.js';
import '../katapult-elements/katapult-icon.js';
import '../katapult-elements/katapult-button.js';
import { capitalCase } from 'change-case';
import { FormatDate, HowLong } from '../../modules/Date.js';
import { config } from '../../app-configuration/config.js';
import '@lit-labs/virtualizer';

/** @import { Ref } from 'lit/directives/ref.js' */
/** @import { LitVirtualizer } from '@lit-labs/virtualizer' */

class AdminDashboardUsersList extends LitElement {
  static properties = {
    companies: { type: Object },
    companyId: { type: String },
    currentUserData: { type: Object },
    configOptions: { type: Object },
    durationTrigger: { type: Boolean },
    enabledFeatures: { type: Object },
    externalUsers: { type: Array },
    filterString: { type: String },
    selectedUserUid: { type: String },
    serverTimeOffset: { type: Number },
    showEmails: { type: Boolean },
    subscription: { type: Object },
    users: { type: Array },
    userListMetadataAttributes: { type: Array },

    _virtualizedItemsArray: { type: Array, state: true },
    _virtualizerVisibility: { type: Object, state: true },
    _currentHeaderIndex: { type: Number, state: true },
    _upcomingHeaderIndex: { type: Number, state: true }
  };

  static styles = css`
    [hidden] {
      display: none !important;
    }
    :host {
      flex-grow: 1;
    }
    paper-table {
      height: 100%;
    }
    lit-virtualizer {
      flex-grow: 1;
    }
    paper-row.header {
      background-color: var(--secondary-color);
      color: var(--secondary-color-text-color);
      text-transform: uppercase;
      padding-right: 8px;
    }
    paper-row > paper-cell:first-of-type {
      width: 64px;
      flex-grow: 0;
      flex-basis: initial;
    }
    paper-cell.checkbox {
      width: 100px;
      flex-grow: 0;
      flex-basis: initial;
      justify-content: center;
      align-items: center;
    }
    paper-cell.checkbox > paper-checkbox {
      --paper-checkbox-label: {
        display: none;
      };
    }
    paper-cell.approveDenyButtons {
      width: 152px;
      flex-grow: 0;
      flex-basis: initial;
      gap: 8px;
    }
    paper-cell.approveDenyButtons katapult-button {
      min-width: 50px;
    }
    .licensedColumnBackground {
      background-color: var(--paper-blue-50);
    }
    .userRow {
      border-bottom: 1px solid var(--paper-grey-200);
      user-select: none;
    }
    .userRow:hover {
      z-index: 1;
    }
    .userRow:hover:not([pending]),
    .userRow[selected]:not([pending]) {
      background-color: var(--paper-grey-200);
    }
    .userRow[pending] paper-cell:not(.approveDenyButtons) {
      opacity: 0.5;
    }
    .userRow[pending] paper-cell.email {
      font-style: italic;
    }
    highlight-text {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2; /* number of lines to show */
      line-height: 18px; /* fallback */
      max-height: 36px; /* fallback */
      --highlight-text-highlight-background-color: var(--secondary-color);
      --highlight-text-highlight-color: var(--secondary-color-text-color);
      --highlight-text-highlight-padding: 0px 2px;
      --highlight-text-highlight-border-radius: 4px;
      --highlight-text-match-color: var(--primary-text-color);
      --highlight-text-filter-color: var(--paper-grey-600);
    }
  `;

  constructor() {
    super();

    this.companies = null;
    this.companyId = null;
    this.configOptions = null;
    this.currentUserData = null;
    this.durationTrigger = false;
    this.enabledFeatures = null;
    this.externalUsers = [];
    this.filterString = null;
    this.selectedUserUid = null;
    this.serverTimeOffset = 0;
    this.showEmails = false;
    this.subscription = null;
    this.users = [];
    this.userListMetadataAttributes = null;

    this._virtualizedItemsArray = [];
    this._virtualizerVisibility = { first: 0, last: 0 };
    this._virtualizerRange = { first: 0, last: 0 };
    this._currentHeaderIndex = -1;
    this._upcomingHeaderIndex = -1;

    /** @type {Ref<HTMLElement>} */
    this._stickyHeaderRef = createRef();

    /** @type {Ref<Element>} */
    this._scrollerRef = createRef();

    /** @type {Ref<LitVirtualizer>} */
    this._virtualizerRef = createRef();
  }

  #renderItem(item) {
    const { template } = item;

    const {
      showEmails,
      configOptions,
      subscription,
      userListMetadataAttributes,
      currentUserData,
      enabledFeatures,
      companyId,
      filterString,
      selectedUserUid
    } = this;
    const {
      show_first_name: showFirstName = false,
      show_last_name: showLastName = false,
      show_user_date_created: showUserDateCreated = false
    } = configOptions ?? {};
    const { group_attribute: subscriptionGroupAttribute = null, show_last_active: subscriptionShowLastActive = false } = subscription ?? {};
    const { workflows } = currentUserData ?? {};
    const showReadWriteLicensedCheckboxes = workflows?.engineering_tools || workflows?.pole_app_portal;

    switch (template) {
      case 'EXTERNAL_USER_HEADER': {
        return html`
          <paper-row class="header" style="background: var(--paper-grey-300); color: var(--primary-text-color);">
            <paper-cell style="justify-content: center;"><katapult-icon icon="background_replace"></katapult-icon></paper-cell>
            <paper-cell class="email">External Users</paper-cell>
            ${showReadWriteLicensedCheckboxes
              ? html`
                  <paper-cell class="checkbox">Read</paper-cell>
                  ${enabledFeatures?.cross_server_identity ? html`<paper-cell class="checkbox"></paper-cell>` : nothing}
                  <paper-cell class="checkbox">Write</paper-cell>
                `
              : nothing}
            <paper-cell class="checkbox">Admin</paper-cell>
            <paper-cell icon></paper-cell>
            <paper-cell class="approveDenyButtons"></paper-cell>
          </paper-row>
        `;
      }
      case 'EXTERNAL_USER_ROW': {
        const user = item;
        const openExternalUserDialog = (e) => {
          this.#fire('open-external-user-dialog', { user });
        };
        const removeExternalUser = (e) => {
          e.stopPropagation();
          this.#fire('remove-external-user', { user });
        };
        return html`
          <paper-row class="userRow" @click=${openExternalUserDialog}>
            <paper-cell>
              <user-chip .email=${user.email} hide-name></user-chip>
            </paper-cell>
            ${showEmails
              ? html`
                  <paper-cell class="email">
                    <highlight-text filter=${filterString}>
                      <span>${user.email}</span>
                    </highlight-text>
                  </paper-cell>
                `
              : nothing}
            ${showReadWriteLicensedCheckboxes
              ? html`
                  <paper-cell class="checkbox">
                    <paper-checkbox .checked=${user.read_permission} disabled></paper-checkbox>
                  </paper-cell>
                  ${enabledFeatures?.cross_server_identity ? html`<paper-cell class="checkbox"></paper-cell>` : nothing}
                  <paper-cell class="checkbox">
                    <paper-checkbox .checked=${user.write_permission} disabled></paper-checkbox>
                  </paper-cell>
                  <paper-cell class="checkbox">
                    <paper-checkbox .checked=${user.company_admin} disabled></paper-checkbox>
                  </paper-cell>
                `
              : nothing}
            <paper-cell icon>
              <sl-tooltip placement="bottom" hoist>
                <katapult-icon icon="domain"></katapult-icon>
                <div slot="content">This user is part of another company, but has access to ${this.#getCompanyName(companyId)}</div>
              </sl-tooltip>
            </paper-cell>
            <paper-cell style="justify-content: flex-end;" class="approveDenyButtons">
              <katapult-button icon="block" iconOnly noBorder @click=${removeExternalUser}></katapult-button>
            </paper-cell>
          </paper-row>
        `;
      }
      case 'USERS_HEADER':
        return html`
          <paper-row class="header">
            <paper-cell style="justify-content: center;">
              <katapult-icon icon="person"></katapult-icon>
            </paper-cell>
            ${showEmails ? html`<paper-cell class="email">Email</paper-cell>` : nothing}
            ${showFirstName ? html`<paper-cell>First Name</paper-cell>` : nothing}
            ${showLastName ? html`<paper-cell>Last Name</paper-cell>` : nothing}
            ${subscriptionGroupAttribute ? html`<paper-cell>${capitalCase(subscriptionGroupAttribute)}</paper-cell>` : nothing}
            <!-- Display any user metadata attributes that should be displayed in the user list// Show any attributes from the options -->
            ${userListMetadataAttributes?.map(({ attribute }) => html`<paper-cell>${capitalCase(attribute)}</paper-cell>`)}
            ${subscriptionShowLastActive ? html`<paper-cell>Last Active</paper-cell>` : nothing}
            ${showUserDateCreated ? html`<paper-cell>Date Created</paper-cell>` : nothing}
            ${showReadWriteLicensedCheckboxes
              ? html`
                  <paper-cell class="checkbox">Read</paper-cell>
                  ${enabledFeatures?.cross_server_identity ? html`<paper-cell class="checkbox">Licensed</paper-cell>` : nothing}
                  <paper-cell class="checkbox">Write</paper-cell>
                `
              : nothing}
            <paper-cell class="checkbox">Admin</paper-cell>
            ${enabledFeatures?.project_manager_role ? html`<paper-cell class="checkbox">Project Manager</paper-cell>` : nothing}
            ${!companyId ? html`<paper-cell>Company</paper-cell>` : nothing}
            <paper-cell icon></paper-cell>
            <paper-cell class="approveDenyButtons"></paper-cell>
            <!-- Template Repeat Here -->
          </paper-row>
        `;
      case 'USER_ROW': {
        const user = item;
        const userIsProjectManager = user.roles?.photoheight?.project_manager;
        const selectUser = () => {
          this.#fire('select-user', { user });
        };
        const promptApproveUser = () => {
          this.#fire('prompt-approve-user', { user });
        };
        const promptDenyUser = () => {
          this.#fire('prompt-deny-user', { user });
        };
        return html`
          <paper-row class="userRow" ?selected=${user.uid == selectedUserUid} ?pending=${user.pending_permission} @click=${selectUser}>
            <paper-cell>
              <user-chip .email=${user.email} hide-name></user-chip>
            </paper-cell>
            ${showEmails
              ? html`
                  <paper-cell class="email">
                    <highlight-text .filter=${filterString}>
                      <span>${user.email}</span>
                    </highlight-text>
                  </paper-cell>
                `
              : nothing}
            ${showFirstName
              ? html`
                  <paper-cell>
                    <highlight-text filter=${filterString}>
                      <span>${user.name?.first}</span>
                    </highlight-text>
                  </paper-cell>
                `
              : nothing}
            ${showLastName
              ? html`
                  <paper-cell>
                    <highlight-text filter=${filterString}>
                      <span>${user.name?.last}</span>
                    </highlight-text>
                  </paper-cell>
                `
              : nothing}
            ${subscriptionGroupAttribute
              ? html`
                  <paper-cell>
                    <highlight-text filter=${filterString}>
                      <span>${user[subscriptionGroupAttribute]}</span>
                    </highlight-text>
                  </paper-cell>
                `
              : nothing}
            <!-- Display the list of metadata attribute values for the user -->
            ${userListMetadataAttributes?.map(
              ({ attribute }) => html` <paper-cell><span>${user.metaData?.[attribute]}</span></paper-cell> `
            )}
            ${subscriptionShowLastActive
              ? html`
                  <paper-cell>
                    <span>${this.#calcDuration(user.last_active)}</span>
                  </paper-cell>
                `
              : nothing}
            ${showUserDateCreated
              ? html`
                  <paper-cell>
                    <span>${FormatDate(user.creation_date)}</span>
                  </paper-cell>
                `
              : nothing}
            ${showReadWriteLicensedCheckboxes
              ? html`
                  <paper-cell class="checkbox">
                    <paper-checkbox .checked=${user.read_permission} disabled></paper-checkbox>
                  </paper-cell>
                  ${enabledFeatures?.cross_server_identity
                    ? html`
                        <paper-cell class="checkbox licensedColumnBackground">
                          <paper-checkbox .checked=${user.licensed} disabled></paper-checkbox>
                        </paper-cell>
                      `
                    : nothing}
                  <paper-cell class="checkbox licensedColumnBackground">
                    <paper-checkbox .checked=${user.write_permission} disabled></paper-checkbox>
                  </paper-cell>
                `
              : nothing}
            <paper-cell class="checkbox">
              <paper-checkbox .checked=${user.roles?.photoheight?.company_admin} disabled></paper-checkbox>
            </paper-cell>
            ${enabledFeatures?.project_manager_role
              ? html`
                  <paper-cell class="checkbox">
                    <paper-checkbox .checked=${userIsProjectManager} disabled></paper-checkbox>
                  </paper-cell>
                `
              : nothing}
            ${!companyId
              ? html`
                  <paper-cell>
                    <highlight-text filter=${filterString}>
                      <span>${this.#getCompanyName(user.companyId)}</span>
                    </highlight-text>
                  </paper-cell>
                `
              : nothing}
            <!-- RETURN -->
            <paper-cell icon>
              <sl-tooltip ?hidden=${!this.#shouldShowExternalPermissionsIconForUser(user)} placement="bottom" hoist>
                <katapult-icon icon="domain"></katapult-icon>
                <div
                  slot="content"
                  style="display: flex; flex-direction: column; padding: var(--sl-spacing-x-small); gap: var(--sl-spacing-x-small);"
                >
                  <div>This user has access to the following external companies:</div>
                  ${this.#getUserExternalPermissionsArray(user).map(
                    (server) => html`
                      <div style="display: flex; align-items: center; gap: var(--sl-spacing-small);">
                        <katapult-icon icon="dns" size="20"></katapult-icon>
                        <div>${server.name} Server</div>
                      </div>
                      <ul style="margin: unset;">
                        ${server.externalCompanies.map((company) => html`<li>${company}</li>`)}
                      </ul>
                    `
                  )}
                  <div>Check "Server Access" for the full list of servers this user can access.</div>
                </div>
              </sl-tooltip>
            </paper-cell>
            <paper-cell class="approveDenyButtons">
              ${user.pending_permission
                ? html`
                    <katapult-button size="14" color="var(--paper-green-500)" @click=${promptApproveUser}>Approve</katapult-button>
                    <katapult-button size="14" color="var(--paper-red-500)" @click=${promptDenyUser}>Deny</katapult-button>
                  `
                : nothing}
            </paper-cell>
          </paper-row>
        `;
      }
      default:
        return nothing;
    }
  }

  render() {
    const currentHeader = this._virtualizedItemsArray[this._currentHeaderIndex];
    return html`
      <paper-table>
        <div ${ref(this._stickyHeaderRef)} style="position: absolute; top: 0; left: 0; z-index: 1;">
          ${currentHeader ? this.#renderItem(currentHeader) : nothing}
        </div>
        <div ${ref(this._scrollerRef)} style="overflow: auto; flex-grow: 1;" @scroll=${() => this.#repositionStickyHeader()}>
          <sl-resize-observer @sl-resize=${this.#resizeHandler}>
            <lit-virtualizer
              ${ref(this._virtualizerRef)}
              @visibilityChanged=${this.#virtualizerVisibilityChangedHandler.bind(this)}
              @rangeChanged=${this.#virtualizerRangeChangeHandler.bind(this)}
              .items=${this._virtualizedItemsArray}
              .renderItem=${this.#renderItem.bind(this)}
              .keyFunction=${this.#keyFunction.bind(this)}
            >
            </lit-virtualizer>
          </sl-resize-observer>
        </div>
      </paper-table>
    `;
  }

  shouldUpdate(changedProperties) {
    if (['filterString', 'users', 'externalUsers'].some((x) => changedProperties.has(x))) {
      this.#updateVirtualizedItemsArray();
    }

    if (['_virtualizerVisibility', '_virtualizedItemsArray'].some((x) => changedProperties.has(x))) {
      this.#updateVisibleHeaders();
    }

    if (['_currentHeaderIndex', '_upcomingHeaderIndex', '_virtualizerRange'].some((x) => changedProperties.has(x))) {
      this.#repositionStickyHeader();
    }

    const updateCalledManually = changedProperties.size === 0;
    const propertiesToIgnore = ['_virtualizerVisibility', 'filterString', 'users', 'externalUsers'];
    const propertyChanges = changedProperties.keys().some((key) => !propertiesToIgnore.includes(key));
    const shouldUpdate = updateCalledManually || propertyChanges;
    return shouldUpdate;
  }

  #fire(eventName, detail) {
    this.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  #getCompanyName(companyId) {
    const { companies = {} } = this;
    return companies[companyId]?.name;
  }

  /**
   * Returns the filtered users array for export purposes.
   * @returns {Array<Object>} Returns the filtered users array
   */
  getFilteredUsers() {
    const { users } = this;
    return this.#filterUsersArray(users);
  }
  /**
   * Takes an array of users and filters and sorts them.
   * @param {Array} users
   * @returns {Array}
   */
  #filterUsersArray(users) {
    const { filterString, subscription } = this;
    const sanitizedFilterString = filterString?.trim?.().toLowerCase();
    const { group_attribute: subscriptionGroupAttribute } = subscription ?? {};

    const filteredUsers =
      sanitizedFilterString != null
        ? users.filter((user) => {
            const filterArray = ['email'];
            if (subscriptionGroupAttribute) filterArray.push(subscriptionGroupAttribute);
            return filterArray.some((key) => {
              const propValue = user[key];
              const lowercasePropValue = propValue?.toLowerCase?.();
              if (lowercasePropValue)
                return lowercasePropValue.includes(sanitizedFilterString) || sanitizedFilterString.includes(lowercasePropValue);
            });
          })
        : users;

    const sortedFilteredUsers = filteredUsers.sort((a, b) => {
      if (a.pending_permission && !b.pending_permission) return -1;
      if (b.pending_permission && b.pending_permission) return 1;
      return (a.email ?? '').localeCompare(b.email ?? '');
    });

    return sortedFilteredUsers;
  }

  #updateVirtualizedItemsArray() {
    const _virtualizedItemsArray = [];
    const { users, externalUsers } = this;

    const filteredUsers = this.#filterUsersArray(users);
    const filteredExternalUsers = this.#filterUsersArray(externalUsers);

    const hasExternalUsers = filteredExternalUsers?.length > 0;
    if (hasExternalUsers) {
      _virtualizedItemsArray.push({ template: 'EXTERNAL_USER_HEADER', header: true });
      _virtualizedItemsArray.push(...filteredExternalUsers.map((user) => ({ template: 'EXTERNAL_USER_ROW', ...user })));
    }

    _virtualizedItemsArray.push({ template: 'USERS_HEADER', header: true });
    _virtualizedItemsArray.push(...filteredUsers.map((user) => ({ template: 'USER_ROW', ...user })));

    const _firstHeader = _virtualizedItemsArray.find((x) => x.header);

    Object.assign(this, { _virtualizedItemsArray, _firstHeader });
  }

  #resizeHandler(e) {
    const stickyHeaderElement = this._stickyHeaderRef.value;
    if (!stickyHeaderElement) return;

    const [firstEntry] = e.detail.entries;
    const [firstContentBoxSize] = firstEntry.contentBoxSize;
    const firstInlineSize = firstContentBoxSize.inlineSize;
    stickyHeaderElement.style.width = firstInlineSize + 'px';
  }

  #repositionStickyHeader() {
    const { _virtualizerRef, _virtualizerRange, _scrollerRef, _stickyHeaderRef, _upcomingHeaderIndex } = this;

    const header = _stickyHeaderRef.value;
    if (!header) return;

    const virtualizer = _virtualizerRef.value;
    if (!virtualizer) return;

    const scroller = _scrollerRef.value;
    if (!scroller) return;

    const { first: firstRendered } = _virtualizerRange;

    const upcomingHeaderElement = virtualizer.children[_upcomingHeaderIndex - firstRendered];
    if (!upcomingHeaderElement) {
      header.style.top = '0px';
      return;
    }

    const { height: headerHeight } = header.getBoundingClientRect();
    const { top: scrollerTop } = scroller.getBoundingClientRect();
    const { top: upcomingHeaderTop } = upcomingHeaderElement.getBoundingClientRect();
    const upcomingHeaderDistanceFromScrollerTop = upcomingHeaderTop - scrollerTop;

    const amountUpcomingHeaderOverlapsWithCurrentHeader = headerHeight - upcomingHeaderDistanceFromScrollerTop;
    const headerTop = Math.max(amountUpcomingHeaderOverlapsWithCurrentHeader, 0);
    header.style.top = `${-headerTop}px`;
  }

  #virtualizerVisibilityChangedHandler(e) {
    const { first, last } = e;
    this._virtualizerVisibility = { first, last };
  }

  #virtualizerRangeChangeHandler(e) {
    const { first, last } = e;
    this._virtualizerRange = { first, last };
  }

  #updateVisibleHeaders() {
    const { _virtualizerVisibility, _virtualizedItemsArray } = this;
    const { first, last } = _virtualizerVisibility;
    const currentHeaderIndex = _virtualizedItemsArray.slice(0, first + 1).findLastIndex((x) => x.header);
    const upcomingHeaderRelativeIndex = _virtualizedItemsArray.slice(currentHeaderIndex + 1, last + 1).findIndex((x) => x.header);
    const upcomingHeaderIndex = upcomingHeaderRelativeIndex > -1 ? currentHeaderIndex + 1 + upcomingHeaderRelativeIndex : -1;
    Object.assign(this, { _currentHeaderIndex: currentHeaderIndex, _upcomingHeaderIndex: upcomingHeaderIndex });
  }

  #keyFunction(item) {
    if (item == null) return;
    // Note: This assumes there will only ever be one type of header.
    if (item.header) return item.template;
    // If not a header, this must be a user.
    // Return the uid (or email if this is an external user) as the key.
    return item.uid ?? item.email;
  }

  #calcDuration(lastChanged) {
    if (lastChanged) {
      let x = HowLong(Date.now() + this.serverTimeOffset - lastChanged);
      return x[0];
    }
  }

  #shouldShowExternalPermissionsIconForUser(user) {
    return Object.values(user?.company_permissions ?? {})
      .flatMap((companies) => Object.keys(companies ?? {}))
      .some((key) => key != this.companyId);
  }

  #getUserExternalPermissionsArray(user) {
    const companyPermissions = user?.company_permissions;
    const filteredCompanyPermissionsEntries = Object.entries(companyPermissions ?? {}).filter(([, companiesOnServer]) => {
      const companyIds = Object.keys(companiesOnServer ?? {});
      return companyIds.length > 0 && companyIds.some((companyId) => companyId != this.companyId);
    });
    const companyPermissionsArray = filteredCompanyPermissionsEntries.map(([serverId, companiesOnServer]) => {
      return {
        name: config.serverNames[serverId] ?? serverId,
        externalCompanies: Object.entries(companiesOnServer)
          .filter(([companyId]) => companyId != this.companyId)
          .map(([companyId, { name }]) => name ?? companyId)
      };
    });
    return companyPermissionsArray;
  }
}

customElements.define('admin-dashboard-users-list', AdminDashboardUsersList);
